import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useCreateSnapshot, useNetworthPreview } from "../../hooks/networth";
import type { CreateSnapshotInput } from "../../lib/networth";
import { mapNetworthError } from "../../lib/networth-errors";
import { Modal } from "../ui/Modal";

interface Props {
  open: boolean;
  onClose: () => void;
}

function parseEuroDe(input: string): number {
  const trimmed = input.replace(/\./g, "").replace(",", ".").trim();
  if (!trimmed) return 0;
  const n = Number.parseFloat(trimmed);
  if (Number.isNaN(n)) throw new Error("invalid_amount");
  return Math.round(n * 100);
}

function formatEur(cents: number): string {
  return (cents / 100).toLocaleString("de-DE", { style: "currency", currency: "EUR" });
}

function todayIso(): string {
  const now = new Date();
  return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()))
    .toISOString()
    .slice(0, 10);
}

interface OverrideState {
  enabled: boolean;
  value: string; // string for input control
  note: string;
}

export function SnapshotFormModal({ open, onClose }: Props) {
  const { t } = useTranslation();
  const [stichtag, setStichtag] = useState(todayIso());
  const [committedStichtag, setCommittedStichtag] = useState(todayIso());
  const [immobilie, setImmobilie] = useState("");
  const [immobilieNote, setImmobilieNote] = useState("");
  const [sonstige, setSonstige] = useState("");
  const [sonstigeNote, setSonstigeNote] = useState("");
  const [liquideOverride, setLiquideOverride] = useState<OverrideState>({
    enabled: false,
    value: "",
    note: "",
  });
  const [anlagenOverride, setAnlagenOverride] = useState<OverrideState>({
    enabled: false,
    value: "",
    note: "",
  });
  const [verbOverride, setVerbOverride] = useState<OverrideState>({
    enabled: false,
    value: "",
    note: "",
  });
  const [serverError, setServerError] = useState<string | null>(null);

  const preview = useNetworthPreview(committedStichtag);
  const createSnapshot = useCreateSnapshot();

  function safeParseCents(s: string): number {
    try {
      return parseEuroDe(s);
    } catch {
      return 0;
    }
  }

  function computeTotal(): number {
    let total = 0;
    if (liquideOverride.enabled) {
      total += safeParseCents(liquideOverride.value);
    } else if (preview.data) {
      total += preview.data.derived.LIQUIDE.valueCents;
    }
    if (anlagenOverride.enabled) {
      total += safeParseCents(anlagenOverride.value);
    } else if (preview.data) {
      total += preview.data.derived.ANLAGEN.valueCents;
    }
    // Verbindlichkeiten werden subtrahiert (Networth = Assets - Liabilities).
    if (verbOverride.enabled) {
      total -= safeParseCents(verbOverride.value);
    } else if (preview.data) {
      total -= preview.data.derived.VERBINDLICHKEITEN.valueCents;
    }
    total += safeParseCents(immobilie);
    total += safeParseCents(sonstige);
    return total;
  }

  async function handleSubmit() {
    setServerError(null);
    const input: CreateSnapshotInput = { stichtag };
    if (immobilie.trim() || immobilieNote.trim()) {
      input.manual = input.manual ?? {};
      input.manual.IMMOBILIE = {
        valueCents: safeParseCents(immobilie),
        ...(immobilieNote.trim() ? { note: immobilieNote.trim() } : {}),
      };
    }
    if (sonstige.trim() || sonstigeNote.trim()) {
      input.manual = input.manual ?? {};
      input.manual.SONSTIGE = {
        valueCents: safeParseCents(sonstige),
        ...(sonstigeNote.trim() ? { note: sonstigeNote.trim() } : {}),
      };
    }
    if (liquideOverride.enabled) {
      input.overrides = input.overrides ?? {};
      input.overrides.LIQUIDE = {
        valueCents: safeParseCents(liquideOverride.value),
        ...(liquideOverride.note.trim() ? { note: liquideOverride.note.trim() } : {}),
      };
    }
    if (anlagenOverride.enabled) {
      input.overrides = input.overrides ?? {};
      input.overrides.ANLAGEN = {
        valueCents: safeParseCents(anlagenOverride.value),
        ...(anlagenOverride.note.trim() ? { note: anlagenOverride.note.trim() } : {}),
      };
    }
    if (verbOverride.enabled) {
      input.overrides = input.overrides ?? {};
      input.overrides.VERBINDLICHKEITEN = {
        valueCents: safeParseCents(verbOverride.value),
        ...(verbOverride.note.trim() ? { note: verbOverride.note.trim() } : {}),
      };
    }

    try {
      await createSnapshot.mutateAsync(input);
      onClose();
    } catch (err) {
      const code =
        err instanceof Error && "code" in err ? (err as { code: string }).code : undefined;
      setServerError(
        code ? (t(mapNetworthError(code)) ?? t("errors.unknown")) : t("errors.unknown"),
      );
    }
  }

  return (
    <Modal open={open} onClose={onClose} title={t("networth.form.title")}>
      <div className="space-y-4">
        <label className="block">
          <span className="text-sm font-medium">{t("networth.form.stichtag")}</span>
          <input
            type="date"
            value={stichtag}
            min="2000-01-01"
            max={todayIso()}
            onChange={(e) => setStichtag(e.target.value)}
            onBlur={() => setCommittedStichtag(stichtag)}
            className="block mt-1 border rounded px-2 py-1"
          />
        </label>

        <fieldset className="border rounded p-3">
          <legend className="text-sm font-medium px-1">{t("networth.form.derived")}</legend>
          <OverrideField
            label={t("networth.assetClass.LIQUIDE")}
            previewValue={preview.data?.derived.LIQUIDE.valueCents}
            state={liquideOverride}
            onChange={setLiquideOverride}
          />
          <OverrideField
            label={t("networth.assetClass.ANLAGEN")}
            previewValue={preview.data?.derived.ANLAGEN.valueCents}
            state={anlagenOverride}
            onChange={setAnlagenOverride}
          />
          <OverrideField
            label={t("networth.assetClass.VERBINDLICHKEITEN")}
            previewValue={preview.data?.derived.VERBINDLICHKEITEN.valueCents}
            state={verbOverride}
            onChange={setVerbOverride}
          />
        </fieldset>

        <fieldset className="border rounded p-3">
          <legend className="text-sm font-medium px-1">{t("networth.form.manual")}</legend>
          <ManualField
            label={t("networth.assetClass.IMMOBILIE")}
            value={immobilie}
            note={immobilieNote}
            onValue={setImmobilie}
            onNote={setImmobilieNote}
          />
          <ManualField
            label={t("networth.assetClass.SONSTIGE")}
            value={sonstige}
            note={sonstigeNote}
            onValue={setSonstige}
            onNote={setSonstigeNote}
          />
        </fieldset>

        <div className="text-right text-lg font-semibold">
          {t("networth.form.total")}: {formatEur(computeTotal())}
        </div>

        {serverError && <div className="text-red-700 text-sm">{serverError}</div>}

        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-3 py-1 border rounded">
            {t("common.cancel")}
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            className="px-3 py-1 bg-blue-600 text-white rounded"
            disabled={createSnapshot.isPending}
          >
            {createSnapshot.isPending ? t("common.submitting") : t("networth.form.submit")}
          </button>
        </div>
      </div>
    </Modal>
  );
}

interface OverrideFieldProps {
  label: string;
  previewValue: number | undefined;
  state: OverrideState;
  onChange: (s: OverrideState) => void;
}

function OverrideField({ label, previewValue, state, onChange }: OverrideFieldProps) {
  const { t } = useTranslation();
  return (
    <div className="mb-2">
      <div className="flex items-center justify-between">
        <span className="text-sm">{label}</span>
        <label className="text-xs flex items-center gap-1">
          <input
            type="checkbox"
            checked={state.enabled}
            onChange={(e) =>
              onChange({
                ...state,
                enabled: e.target.checked,
                value: e.target.checked ? state.value : "",
                note: e.target.checked ? state.note : "",
              })
            }
          />
          {t("networth.form.override")}
        </label>
      </div>
      {state.enabled ? (
        <>
          <input
            type="text"
            inputMode="decimal"
            value={state.value}
            onChange={(e) => onChange({ ...state, value: e.target.value })}
            placeholder="0,00"
            className="block mt-1 border rounded px-2 py-1 w-full"
          />
          <input
            type="text"
            value={state.note}
            onChange={(e) => onChange({ ...state, note: e.target.value })}
            placeholder={t("networth.form.note") ?? ""}
            className="block mt-1 border rounded px-2 py-1 w-full text-sm"
          />
        </>
      ) : (
        <div
          className="text-sm text-gray-500 mt-1"
          title={t("networth.form.derived_tooltip") ?? ""}
        >
          {previewValue !== undefined
            ? (previewValue / 100).toLocaleString("de-DE", { style: "currency", currency: "EUR" })
            : "…"}
        </div>
      )}
    </div>
  );
}

interface ManualFieldProps {
  label: string;
  value: string;
  note: string;
  onValue: (v: string) => void;
  onNote: (v: string) => void;
}

function ManualField({ label, value, note, onValue, onNote }: ManualFieldProps) {
  const { t } = useTranslation();
  return (
    <div className="mb-2">
      <span className="text-sm">{label}</span>
      <input
        type="text"
        inputMode="decimal"
        value={value}
        onChange={(e) => onValue(e.target.value)}
        placeholder="0,00"
        className="block mt-1 border rounded px-2 py-1 w-full"
      />
      <input
        type="text"
        value={note}
        onChange={(e) => onNote(e.target.value)}
        placeholder={t("networth.form.note") ?? ""}
        className="block mt-1 border rounded px-2 py-1 w-full text-sm"
      />
    </div>
  );
}
